<script lang="ts" setup generic="TItem extends {}, TColumn extends {}">
import { useStore } from 'vuex'
import { watch, ref, computed, watchEffect } from 'vue'
import { getScreenUtils } from '@/components/lib/GwUtils/utils_screen'
import GwPagination from '@/components/lib/GwPagination/GwPagination.vue'
import type { GwPaginationEmitProps } from '@/components/lib/GwPagination/gwPaginationUtils'
import {
  tableVariantsConf,
  type GwTableProps,
  type GwTableColumn,
  type GwTableCheckBoxState,
} from '@/components/lib/GwTable/gwTableUtils'
import GwVirtualList from '@/components/lib/GwVirtualList/GwVirtualList.vue'
import type { ID } from '@/types/basic'
import LoaderCircles from '@/components/Loaders/LoaderCircles.vue'
import GwTableHead from '@/components/lib/GwTable/GwTableHead.vue'
import GwTableRow from '@/components/lib/GwTable/GwTableRow.vue'
import GwBox from '@/components/lib/GwBox/GwBox.vue'

type Table = GwTableProps<TItem, TColumn>
const props = withDefaults(defineProps<Table>(), {
  gwPaginationProps: () => ({}),
  hasPagination: true,
})

// https://github.com/vuejs/language-tools/issues/3206
// Waiting for the fix
// @ts-expect-error
const virtualListRef = ref<InstanceType<typeof GwVirtualList<TItem>> | null>(null)

const emit = defineEmits<{
  'select:all': []
  'scrolled:end': []
  'select:row': [ID]
  'update:pagination': [GwPaginationEmitProps]
  'update:renderedItems': [number]
  'update:headCheckboxState': [{ headCheckBoxState: GwTableCheckBoxState; selectedItems: ID[] }]
  'th:click': [Event, GwTableColumn<TColumn>]
  'th:mousedown': [Event, GwTableColumn<TColumn>]
  'th:mouseup': [Event, GwTableColumn<TColumn>]
  'th:touchstart': [Event, GwTableColumn<TColumn>]
  'th:touchend': [Event, GwTableColumn<TColumn>]
}>()

// TODO use only [] with idKey values no ''
const selectedItems = ref<ID[]>([])
const store = useStore()
const lastClickedIndex = ref()

const handlePaginationUpdate = (args: GwPaginationEmitProps) => {
  if (virtualListRef.value) {
    virtualListRef.value.scrollToOffset(0)
    emit('update:pagination', args)
  }
}
const getHeadCheckboxState = computed(() =>
  selectedItems.value.length === 0
    ? 'none'
    : selectedItems.value.length === props.items.length
      ? 'all'
      : 'some',
)

const { windowSize } = getScreenUtils()
const rowHeight = computed(() => {
  if (windowSize.value) {
    const fntSizePx = parseFloat(getComputedStyle(document.documentElement).fontSize)
    return fntSizePx * tableVariantsConf[props.variant].row.tr.height
  }
  return 40
})

const handleSelectAll = () => {
  selectedItems.value =
    getHeadCheckboxState.value === 'all' ? [] : props.items.map(i => i[props.idKey] as ID)
}

const selectItem = (id: ID) => {
  const idKey = props.idKey
  const included = selectedItems.value.includes(id)

  selectedItems.value = included
    ? selectedItems.value.filter(i => i !== id)
    : [...selectedItems.value, id]

  // select multiple items when holding shift
  const lastIdx = lastClickedIndex.value
  const index = props.items.findIndex(i => i[idKey] === id)
  if (
    lastIdx !== index &&
    props.items[lastIdx] &&
    store.getters.hotkey_pressed_bottom === 'shift'
  ) {
    const range = (
      lastIdx < index
        ? props.items.slice(lastIdx, index + 1).map(i => i[idKey])
        : props.items.slice(index, lastIdx + 1).map(i => i[idKey])
    ) as ID[]
    selectedItems.value = included
      ? selectedItems.value.filter(i => !range.includes(i))
      : [...selectedItems.value, ...range]
  }
  lastClickedIndex.value = index
}

watch(
  () => props.items,
  () => {
    lastClickedIndex.value = undefined
    if (selectedItems.value.length === 0) return
    const itemIds = new Set(props.items.map(item => item[props.idKey] as ID))
    selectedItems.value = selectedItems.value.filter(id => itemIds.has(id))
  },
)

watchEffect(() => {
  emit('update:headCheckboxState', {
    headCheckBoxState: getHeadCheckboxState.value,
    selectedItems: selectedItems.value,
  })
})

const methods = {
  selectItem,
}
</script>

<template>
  <div
    class="gwtable_wrapper f-column flex-1 mh-0 mw-0"
    style="
      gap: var(--app-gap-1);
      --th-pad-right: var(--app-pad-4);
      --th-pad-left: var(--app-pad-4);
      --clr-loader-back: var(--app-dark-2);
    "
  >
    <div
      style="z-index: 1"
      class="gwtable f-column flex-1 mh-0 mw-0 position-relative"
      :class="variant"
      :data-tst="`${tstPrefix}_table`"
    >
      <slot name="before"></slot>

      <GwVirtualList
        ref="virtualListRef"
        class="flex-1 gwtable_body_container"
        style=""
        :idKey="idKey as keyof TItem"
        :itemSize="rowHeight"
        :items
        :gwScrollContProps="{
          ...props.gwScrollContProps,
          stretched: true,
          direction: 'omni',
          scrollbar: 'large',
        }"
        @scrolled:end="emit('scrolled:end')"
        @update:renderedItems="(num: number) => emit('update:renderedItems', num)"
      >
        <template #default="{ overflow, scrollContStyle, visibleItems, startIndex }">
          <div
            v-if="loading || error || !items.length"
            class="f-center"
            style="
              position: absolute;
              top: 0;
              left: 0;
              height: 100%;
              width: 100%;
              z-index: 10;
              background: var(--clr-loader-back);
            "
          >
            <template v-if="loading">
              <slot name="loading">
                <LoaderCircles
                  size="big"
                  data-tst="table_loader"
                />
              </slot>
            </template>
            <template v-else-if="error">
              <slot name="error"> error-slot </slot>
            </template>
            <template v-else-if="!items.length">
              <slot name="no_data"> no-data-slot </slot>
            </template>
          </div>

          <GwBox
            tag="table"
            class="flex-1"
            :variant="tableVariantsConf[props.variant].table.variant"
            :class="[
              { gwtable_scrolled_top: overflow.top },
              { gwtable_scrolled_right: overflow.right },
              { gwtable_scrolled_bottom: overflow.bottom },
              { gwtable_scrolled_left: overflow.left },
            ]"
            :style="`--gwtable-row-height: ${rowHeight}px`"
          >
            <GwTableHead
              v-if="!error && items.length"
              :checkboxState="getHeadCheckboxState"
              :hasCheckbox="hasCheckboxes"
              :sortConf
              :columns
              :variantConf="tableVariantsConf[props.variant].head"
              @th:click="(e, item) => emit('th:click', e, item)"
              @th:mousedown="(e, item) => emit('th:mousedown', e, item)"
              @th:mouseup="(e, item) => emit('th:mouseup', e, item)"
              @th:touchstart="(e, item) => emit('th:touchstart', e, item)"
              @th:touchend="(e, item) => emit('th:touchend', e, item)"
              @select:all="handleSelectAll"
            >
              <template #thcell_before="{ item }">
                <slot
                  name="thcell_before"
                  :item="item"
                ></slot>
              </template>
            </GwTableHead>
            <tbody>
              <tr
                class="role_spacer"
                :style="`height: ${scrollContStyle['padding-top']}`"
              ></tr>
              <template
                v-for="(item, index) in visibleItems"
                :key="item[idKey]"
              >
                <slot
                  name="row"
                  :item
                  :index="startIndex + index"
                  :selectedItems
                  :methods
                  :rowHeight
                  :variantConf="tableVariantsConf[props.variant].row"
                >
                  <GwTableRow
                    class="gwtable_body_row"
                    isHoverable
                    :hasCheckbox="hasCheckboxes"
                    :hasNote="hasNotes"
                    :hasColor="hasColors"
                    :selectedItems
                    :variantConf="tableVariantsConf[props.variant].row"
                    @click:checkbox="methods.selectItem"
                  />
                </slot>
              </template>
              <tr
                class="role_spacer"
                :style="`height: ${scrollContStyle['padding-bottom']}`"
              ></tr>
            </tbody>
            <slot
              name="default_after"
              :rowHeight
              :variantConf="tableVariantsConf[props.variant].row"
            >
              <tbody class="h-100"></tbody>
            </slot>
          </GwBox>
        </template>
        <template #content_after>
          <slot name="content_after"> </slot>
        </template>
      </GwVirtualList>
    </div>
    <GwPagination
      v-if="hasPagination"
      style="z-index: 2"
      class="ml-auto"
      v-bind="gwPaginationProps"
      :data-tst="`${tstPrefix}_pagination`"
      @update:pagination="handlePaginationUpdate"
    />
  </div>
</template>

<style lang="scss">
.gwtable_th_sticky_left,
.gwtable_td_sticky_left {
  position: sticky;
  left: 0;
}
.gwtable_th_sticky_left {
  z-index: 6;
}
.gwtable_td_sticky_left {
  z-index: 5;
}
.gwtable_scrolled_left {
  .gwtable_th_sticky_left,
  .gwtable_td_sticky_left {
    background: var(--app-surface-table);
  }
}

.gwtable_scrolled_top {
}
</style>

<style lang="scss" scoped>
td,
th {
  text-align: left;
}

.table_default {
  .gwtable_body_container {
    background: var(--app-clr-surface-table);
    border-radius: 0 0 var(--app-rad-4) var(--app-rad-4);
  }
}

.table_fat {
  .gwtable_body_container {
    background: transparent;
  }
}
</style>
